import React from "react";
import { Helmet } from "react-helmet";
import { motion } from 'framer-motion';
import { Instagram, Twitter, Youtube, Music2, ExternalLink, Quote, Heart, Users, BookOpen, Globe } from 'lucide-react';

const TitoTheWriterPage = () => {
  const socialLinks = [
    {
      platform: 'Instagram',
      handle: '@titothewriter',
      url: 'https://www.instagram.com/titothewriter/',
      icon: Instagram,
      color: 'hover:text-pink-500'
    },
    {
      platform: 'Twitter (X)',
      handle: '@titothewriter',
      url: 'https://twitter.com/titothewriter',
      icon: Twitter,
      color: 'hover:text-blue-400'
    },
    {
      platform: 'YouTube',
      handle: '@titothewriter',
      url: 'https://www.youtube.com/@titothewriter',
      icon: Youtube,
      color: 'hover:text-red-500'
    },
    {
      platform: 'TikTok',
      handle: '@titothewriter1',
      url: 'https://www.tiktok.com/@titothewriter1',
      icon: Music2,
      color: 'hover:text-purple-500'
    }
  ];

  const impactStats = [
    {
      icon: Heart,
      number: '10K+',
      label: 'Lives Impacted',
      color: 'text-red-400'
    },
    {
      icon: Users,
      number: '50+',
      label: 'Global Events',
      color: 'text-blue-400'
    },
    {
      icon: BookOpen,
      number: '1000+',
      label: 'Written Articles',
      color: 'text-green-400'
    },
    {
      icon: Globe,
      number: '20+',
      label: 'Countries Reached',
      color: 'text-purple-400'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-0"
    >
      {/* Hero Section */}
      <div className="relative overflow-hidden py-24">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://tsoministries.com/wp-content/uploads/2025/05/@titothewriterd.jpeg')] opacity-100 bg-cover bg-center md:bg-[center_50%]"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/60 to-black"></div>
        </div>

        <div className="container mx-auto px-4 py-16 relative">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 mt-12">
              Meet Titoluwa
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-pink-400">
                {" "}TitoTheWriter{" "}
              </span>
              Olumideko
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Founder of TSO Ministries, Proclaiming Christ and raising kingdom-minded believers across nations.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative container mx-auto px-4 py-16">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://tsoministries.com/wp-content/uploads/2025/05/@titothewriter2.jpeg')] opacity-50 bg-cover bg-center md:bg-[center_50%]"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/60 to-black"></div>
        </div>
        <div className="relative max-w-4xl mx-auto">
          {/* About Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="prose prose-lg prose-invert">
              <p className="text-xl leading-relaxed text-gray-300 mb-6">
                Known by many as <span className="text-purple-400">Titoluwa Ministries</span>, her ministry has impacted thousands through inspired writings, prophetic teachings, and global outreach campaigns. Whether you see her listed as <em>TitoTheWriter</em>, <em>TSO Ministries</em>, or <em>Titoluwa Olumideko Ministries</em>, it all leads back to one mandate: <em className="text-pink-400">"Till every eye sees Jesus."</em>
              </p>
              
              <p className="text-xl leading-relaxed text-gray-300">
                Tito is not only a messenger but a mentor, helping young people discover their calling, strengthen their spiritual walk, and walk in divine authority. She began sharing her writings as "TitoTheWriter", which soon gained traction online, turning posts into platforms, and blogs into blueprints for global revival.
              </p>
            </div>
          </motion.div>

          {/* Impact Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
          >
            {impactStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 text-center border border-white/10"
              >
                <stat.icon className={`w-8 h-8 ${stat.color} mx-auto mb-4`} />
                <h3 className="text-3xl font-bold mb-2">{stat.number}</h3>
                <p className="text-gray-400">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Names Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 mb-16"
          >
            <h2 className="text-2xl font-bold mb-6">Names You Might Know</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                'TitoTheWriter',
                'TSO Ministries',
                'Titoluwa Ministries',
                'Titoluwa Olumideko',
                'Tito Oladapo'
              ].map((name, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/5 rounded-lg p-4 text-center"
                >
                  {name}
                </motion.div>
              ))}
            </div>
            <p className="text-gray-300 mt-6 text-center">
              No matter the name, the heart remains the same: pointing this generation to Jesus Christ with boldness, truth, and compassion.
            </p>
          </motion.div>

          {/* Social Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold mb-6">Connect with TitoTheWriter</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center space-x-4 bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 ${social.color} transition-colors duration-300`}
                >
                  <social.icon className="w-6 h-6" />
                  <div>
                    <p className="font-medium">{social.platform}</p>
                    <p className="text-sm text-gray-400">{social.handle}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 ml-auto" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quote */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <Quote className="absolute -top-4 -left-4 w-8 h-8 text-purple-400 opacity-50" />
            <blockquote className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-center">
              <p className="text-2xl font-serif italic mb-4">
                "Till every eye sees Jesus."
              </p>
              <footer className="text-gray-400">
                Titoluwa Olumideko
              </footer>
            </blockquote>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};


export default TitoTheWriterPage;